import { useEffect } from 'react';
import { Alert, App, Card, Flex, Space, Tag, theme, Typography } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { plansQueryKeys, useActivatePlan, useDeletePlan, useGetPlans } from '@/features/agreements/hooks/usePlans';
import { useModal } from '@/shared/hooks/useModal';
import { ActionsDropdown } from '@/shared/components/ActionsDropdown';
import { PlanFormModal } from './PlanFormModal';
import { EmptyStateCard } from '../EmptyStateCard';
import { SectionHeader } from '../SectionHeader';
import { LoadingIndicator } from '@/shared/components/LoadingIndicator';
import { ErrorMessage } from '@/shared/components/ErrorMessage';
import { getErrorMessage } from '@/shared/utils/errors/getErrorMessage';
import { getToggleActiveMenuItem } from '@/shared/utils/menuItems/getToggleActiveMenuItem';
import { AddPlanButton } from './AddPlanButton';

const { Text } = Typography;

export const PlansStep = ({ agreementId, onNextRef, readOnly = false }) => {
  const { token: themeToken } = theme.useToken();
  const { notification, modal } = App.useApp();
  const planModal = useModal(null);

  const queryClient = useQueryClient();
  const plansQuery = useGetPlans({ agreementId });
  const activatePlanMutation = useActivatePlan();
  const deletePlanMutation = useDeletePlan();

  const plans = plansQuery.data ?? [];
  const hasPlans = plans.length > 0;

  const handleNext = async () => {
    if (readOnly) return;
    if (!hasPlans) {
      notification.error({
        message: <Text strong>Agrega al menos un plan</Text>,
        description: 'Es necesario crear al menos un plan para continuar con la configuración del convenio.',
      });
      return false;
    }
  };

  const handleAddPlan = () => {
    planModal.open(null);
  };

  const handleRenamePlan = (plan) => {
    planModal.open(plan);
  };

  const handleActivatePlan = (plan) => {
    modal.confirm({
      title: '¿Seguro que deseas activar el plan?',
      content: (
        <Text>
          El plan <Text italic>{plan.name}</Text> estará disponible nuevamente.
        </Text>
      ),
      okText: 'Sí, activar',
      onOk: async () => {
        await activatePlanMutation.mutateAsync({ agreementId, planId: plan.id }, {
          onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: plansQueryKeys.list({ agreementId }) });
            notification.success({
              message: <Text strong>Plan activado</Text>,
              description: '¡El plan se ha activado correctamente!',
            });
          },
          onError: (error) => {
            notification.error({
              message: <Text strong>Plan no activado</Text>,
              description: getErrorMessage(error),
            });
          },
        });
      },
    });
  };

  const handleInactivatePlan = (plan) => {
    modal.confirm({
      title: '¿Seguro que deseas inactivar el plan?',
      content: (
        <Text>
          El plan <Text italic>{plan.name}</Text> dejará de estar disponible. Podrás activarlo más adelante si lo necesitas.
        </Text>
      ),
      okText: 'Sí, inactivar',
      onOk: async () => {
        await deletePlanMutation.mutateAsync({ agreementId, planId: plan.id }, {
          onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: plansQueryKeys.list({ agreementId }) });
            notification.success({
              message: <Text strong>Plan inactivado</Text>,
              description: '¡El plan se ha inactivado correctamente!',
            });
          },
          onError: (error) => {
            notification.error({
              message: <Text strong>Plan no inactivado</Text>,
              description: getErrorMessage(error),
            });
          },
        });
      },
    });
  };

  useEffect(() => {
    if (!onNextRef) return;
    onNextRef.current = handleNext;
    return () => {
      onNextRef.current = null;
    };
  }, [handleNext, onNextRef]);

  if (plansQuery.isLoading) return <LoadingIndicator />;

  if (plansQuery.isError) return <ErrorMessage error={plansQuery.error} />;

  return (
    <Flex justify='center' style={{ paddingInline: '5%' }}>
      <Flex vertical gap='large' flex={1}>
        <Flex justify='space-between' align='flex-end' wrap='wrap' gap='small'>
          <SectionHeader
            title='Creación de planes'
            description='Define los diferentes planes que ofrecerá este convenio.'
          />
          {hasPlans && !readOnly && <AddPlanButton onClick={handleAddPlan} style={{ marginLeft: 'auto' }} />}
        </Flex>
        {hasPlans ? (
          <Flex vertical>
            {plans.map((plan) => {
              const isActive = plan.active;

              const menuItems = [
                {
                  key: 'rename',
                  label: 'Renombrar',
                  onClick: () => handleRenamePlan(plan),
                },
                getToggleActiveMenuItem({
                  record: plan,
                  onActivate: handleActivatePlan,
                  onInactivate: handleInactivatePlan,
                }),
              ];

              return (
                <Card
                  key={plan.id}
                  size='small'
                  style={{
                    paddingInline: '3%',
                    marginTop: -1,
                    borderColor: '#d9d9d9',
                    backgroundColor: 'transparent',
                  }}>
                  <Flex justify='space-between' align='center'>
                    <Space>
                      <Text>{plan.name}</Text>
                      <Tag color={isActive ? 'green' : 'red'}>
                        {isActive ? 'Activo' : 'Inactivo'}
                      </Tag>
                    </Space>
                    {!readOnly && <ActionsDropdown items={menuItems} />}
                  </Flex>
                </Card>
              );
            })}
          </Flex>
        ) : (
          <EmptyStateCard
            title='No hay planes definidos'
            description='Los planes representan diferentes niveles de cobertura. Agrega al menos un plan para continuar con la configuración del convenio.'
            action={!readOnly ? <AddPlanButton onClick={handleAddPlan} /> : null}
          />
        )}
        <Alert
          type='info'
          showIcon
          message={<Text strong style={{ fontSize: 'inherit', color: themeToken.colorInfo }}>Recomendaciones</Text>}
          description='Usa nombres descriptivos como "Plan Básico" "Plan Estándar" que sean fáciles de entender. Es necesario crear al menos un plan, para continuar con la configuración de coberturas, tasas, etc.'
          closable
        />
      </Flex>
      <PlanFormModal
        open={planModal.isOpen}
        onClose={planModal.close}
        agreementId={agreementId}
        plan={planModal.data}
      />
    </Flex>
  );
};
